// /communities/:slug/newsroom — the editor's operational workspace.
// 3-column kanban: FEEDS (raw items from feed_sources, all communities)
// → QUEUE (pending posts in THIS community) → LIVE (approved, 7 days).
//
// Actions on queue cards: Approve · Reject · Return · Merge into… · drop
// image. Merge posts the pending draft into a target's timeline as an
// inline "Update HH:MM: …" line + drops the draft from the queue.
//
// Newsroom stays operational; the older /dashboard page continues to
// host stats + editors + boundary (settings-like stuff).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import { renderMarkdown } from '../lib/markdown';
import { PostMap } from '../components/PostMap';
import { EditorBar } from '../components/EditorBar';
import type { Post } from '../types/api';

interface FeedItem {
    id:            string;
    source_name:   string;
    source_kind:   string;
    title:         string;
    body?:         string;
    link?:         string;
    fetched_at:    string;
    published_at?: string | null;
}
interface QueueItem {
    post_id:        string;
    title:          string;
    summary?:       string;
    body_snippet:   string;
    media_url?:     string;
    submitted_at:   string;
    author_display: string;
    author_is_bot:  boolean;
    update_count:   number;
    topic?:         string | null;
}
interface LiveItem {
    post_id:       string;
    title:         string;
    summary?:      string;
    body_snippet:  string;
    media_url?:    string;
    approved_at:   string;
    update_count:  number;
    like_count:    number;
    comment_count: number;
    topic?:        string | null;
}
interface AgentActivity {
    time:    string;
    agent:   string;
    kind:    string;   // "cost" | "published" | "updated" | "skipped_duplicate" | "skipped_not_relevant" | "skipped_too_old" | "parse_error" | "reviewed"
    title:   string;
    reason?: string;
    post_id?:      string | null;
    feed_item_id?: string | null;
    link?:         string | null;
}
interface NewsroomResponse {
    feeds:          FeedItem[];
    queue:          QueueItem[];
    live:           LiveItem[];
    agent_activity: AgentActivity[];
    queue_has_more?:   boolean;
    queue_next_before?: string | null;
}

interface Topic {
    slug:                 string;
    name:                 string;
    mode:                 'activity' | 'rewrite';
    icon?:                string | null;
    color?:               string | null;
    active:               boolean;
    default_photo_query?: string | null;
    created_at:           string;
}

interface Reporter {
    agent_id:      string;
    agent_name:    string;
    role:          string;
    active:        boolean;
    autopublish:   boolean;
    paused_reason?: string | null;
}

export function CommunityNewsroom() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const { authed, login } = useAuth();
    const [data, setData] = useState<NewsroomResponse | null>(null);
    // Topics are still loaded here (not just in Settings) because the
    // merge modal needs the active topic list to offer "merge into today's
    // topic article" as a target.
    const [topics, setTopics] = useState<Topic[]>([]);
    const [err, setErr]   = useState('');
    const [mergeSource, setMergeSource] = useState<QueueItem | null>(null);
    // Prompt modals — replace ugly window.prompt(). Kept as one shared piece
    // of state; only one prompt open at a time.
    const [textPrompt, setTextPrompt] = useState<{
        title: string;
        placeholder?: string;
        confirmLabel?: string;
        onConfirm: (value: string) => void;
    } | null>(null);
    // Confirm modal — replaces window.confirm().
    const [confirmPrompt, setConfirmPrompt] = useState<{
        title: string;
        detail?: string;
        confirmLabel?: string;
        danger?: boolean;
        onConfirm: () => void;
    } | null>(null);
    // Preview modal — click a queue/live card body to read the whole post
    // without leaving the newsroom. Fetches /api/posts/:id lazily.
    const [previewPostID, setPreviewPostID] = useState<string | null>(null);
    // Queue settings modal — click the "Queue" column header to open.
    // Lists every reporter agent scoped to this community with a per-
    // agent autopublish toggle. Loaded lazily on first open.
    const [queueSettingsOpen, setQueueSettingsOpen] = useState(false);

    const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
    const load = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            const [board, tRes] = await Promise.all([
                api.get<NewsroomResponse>('/api/communities/' + slug + '/newsroom'),
                api.get<{ topics: Topic[] }>('/api/communities/' + slug + '/topics'),
            ]);
            setData(board);
            setTopics(tRes.topics ?? []);
            setLastLoadedAt(Date.now());
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, slug]);
    useEffect(() => { load(); }, [load]);

    // Live-refresh every 15s so the kanban + activity strip stay current
    // without the editor having to click refresh. Skip when the tab is
    // hidden (saves bandwidth + Claude cost if backend triggers auto-poll
    // on read) or when a mutation modal is open (would clobber in-progress
    // edits and blow up the merge/prompt state).
    useEffect(() => {
        const REFRESH_MS = 15_000;
        const t = setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            if (mergeSource || textPrompt || confirmPrompt || queueSettingsOpen) return;
            load();
        }, REFRESH_MS);
        return () => clearInterval(t);
    }, [load, mergeSource, textPrompt, confirmPrompt, queueSettingsOpen]);

    // Infinite scroll: append more queue items when the user scrolls past
    // the current tail. Cursor is the oldest submitted_at we've received.
    const loadMoreQueue = useCallback(async () => {
        if (!data || !data.queue_has_more || !data.queue_next_before) return;
        try {
            const more = await api.get<NewsroomResponse>(
                `/api/communities/${slug}/newsroom?before=${encodeURIComponent(data.queue_next_before)}`);
            setData(d => d ? ({
                ...d,
                queue: [...d.queue, ...more.queue],
                queue_has_more: more.queue_has_more,
                queue_next_before: more.queue_next_before,
            }) : d);
        } catch (e) { console.error('load more', e); }
    }, [api, slug, data]);

    async function decide(postID: string, decision: 'approve'|'reject'|'return', reason?: string) {
        try {
            await api.post(`/api/communities/${slug}/posts/${postID}/decide`,
                { status: decision === 'approve' ? 'approved' : (decision === 'reject' ? 'rejected' : 'returned'), reason });
            load();
        } catch (e) { alert('decide failed: ' + (e as Error).message); }
    }
    // Retriage — clears an item's triage state + geocode data so the
    // reporter re-processes it on the next tick. Used from activity
    // rows to diagnose why an item was skipped or why geocoding failed.
    async function retriage(feedItemID: string) {
        try {
            await api.post(`/api/communities/${slug}/feed-items/${feedItemID}/retriage`, {});
            load();
        } catch (e) { alert('retriage failed: ' + (e as Error).message); }
    }
    async function archive(postID: string) {
        try {
            await api.post(`/api/communities/${slug}/posts/${postID}/archive`, {});
            load();
        } catch (e) { alert('archive failed: ' + (e as Error).message); }
    }
    async function mergeIntoPost(source: QueueItem, target: QueueItem | LiveItem, delta: string) {
        try {
            await api.post(`/api/communities/${slug}/posts/${source.post_id}/merge`,
                { target_post_id: target.post_id, delta_text: delta || undefined });
            setMergeSource(null);
            load();
        } catch (e) { alert('merge failed: ' + (e as Error).message); }
    }
    async function mergeIntoTopic(source: QueueItem, topicSlug: string, delta: string) {
        try {
            await api.post(`/api/communities/${slug}/posts/${source.post_id}/merge`,
                { target_topic: topicSlug, delta_text: delta || undefined });
            setMergeSource(null);
            load();
        } catch (e) { alert('merge failed: ' + (e as Error).message); }
    }
    async function setMedia(postID: string, mediaURL: string) {
        try {
            await api.patch(`/api/communities/${slug}/posts/${postID}/media`, { media_url: mediaURL });
            load();
        } catch (e) { alert('image update failed: ' + (e as Error).message); }
    }
    function dropImage(postID: string) {
        setConfirmPrompt({
            title: 'Remove cover image?',
            detail: 'This drops the current image from the post. You can add a new one later.',
            confirmLabel: 'Remove',
            danger: true,
            onConfirm: () => setMedia(postID, ''),
        });
    }
    function replaceImage(postID: string) {
        setTextPrompt({
            title: 'New cover image URL',
            placeholder: 'https://images.pexels.com/…',
            confirmLabel: 'Set image',
            onConfirm: (v) => v && setMedia(postID, v),
        });
    }
    function askReject(postID: string) {
        setTextPrompt({
            title: 'Reject this post',
            placeholder: 'Reason (optional) — e.g. off-topic, ad, unverified',
            confirmLabel: 'Reject',
            onConfirm: (v) => decide(postID, 'reject', v || undefined),
        });
    }
    function askReturn(postID: string) {
        setTextPrompt({
            title: 'Return for revision',
            placeholder: 'Note to author (optional) — e.g. add event time, verify claim',
            confirmLabel: 'Return',
            onConfirm: (v) => decide(postID, 'return', v || undefined),
        });
    }
    function askArchive(postID: string, title: string) {
        setConfirmPrompt({
            title: 'Archive this post?',
            detail: `"${title}" will disappear from the queue and public feed. Historical data is preserved for audit.`,
            confirmLabel: 'Archive',
            danger: true,
            onConfirm: () => archive(postID),
        });
    }

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see the newsroom.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (err) return <main style={{ padding: 40 }}><p style={{ color: '#8b1919' }}>{err}</p></main>;
    if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1400, margin: '0 auto' }}>
            <div className="masthead" style={{ marginBottom: 12 }}>
                <div className="rule-top" />
                <h1>Newsroom · {slug}</h1>
                <div className="issue">Feeds → Queue → Live · editorial workspace</div>
                <div className="rule-bottom" />
            </div>
            <nav style={navStyle}>
                <Link to={`/communities/${slug}`}           style={navLink}>← Read paper</Link>
                <Link to={`/communities/${slug}/dashboard`} style={navLink}>Dashboard (stats)</Link>
                <Link to={`/communities/${slug}/newsroom`}  style={{ ...navLink, fontWeight: 700 }}>Newsroom</Link>
                <Link to={`/communities/${slug}/layout`}    style={navLink}>Layout</Link>
                <Link to={`/communities/${slug}/settings`}  style={navLink}>Settings</Link>
                <button type="button" className="plain" onClick={load} style={{ marginLeft: 'auto' }}>refresh</button>
                <LiveIndicator lastLoadedAt={lastLoadedAt} />
            </nav>

            <div style={boardStyle}>
                <Column title={`Feeds (${data.feeds.length})`} hint="Raw signals from all sources. Reporter picks from these.">
                    {data.feeds.length === 0 && <Empty text="Nothing untriaged. Feeds catch up every 30 min." />}
                    {data.feeds.map(f => <FeedCard key={f.id} item={f} />)}
                </Column>
                <Column title={`Queue (${data.queue.length}${data.queue_has_more ? '+' : ''})`}
                    hint="Pending drafts, newest first. Scroll for older. Click header for autopublish."
                    onHeaderClick={() => setQueueSettingsOpen(true)}>
                    {data.queue.length === 0 && <Empty text="No pending drafts. Reporter is caught up." />}
                    {groupByDay(data.queue).map(g => (
                        <div key={g.day}>
                            <div style={dayHeader}>{g.day}</div>
                            {g.items.map(q => (
                                <QueueCard key={q.post_id} item={q}
                                    onApprove={() => decide(q.post_id, 'approve')}
                                    onReject={() => askReject(q.post_id)}
                                    onReturn={() => askReturn(q.post_id)}
                                    onMerge={() => setMergeSource(q)}
                                    onArchive={() => askArchive(q.post_id, q.title)}
                                    onDropImage={() => dropImage(q.post_id)}
                                    onReplaceImage={() => replaceImage(q.post_id)}
                                    onPreview={() => setPreviewPostID(q.post_id)}
                                />
                            ))}
                        </div>
                    ))}
                    {data.queue_has_more && (
                        <button type="button" className="plain" onClick={loadMoreQueue}
                            style={{ margin: '8px auto', display: 'block' }}>load older drafts →</button>
                    )}
                </Column>
            </div>

            <div style={{ marginTop: 16 }}>
                <ActivityStrip activity={data.agent_activity ?? []} onRetry={retriage} />
            </div>

            {mergeSource && (
                <MergeModal
                    source={mergeSource}
                    candidates={[...data.queue.filter(q => q.post_id !== mergeSource.post_id), ...data.live]}
                    topics={topics.filter(t => t.active)}
                    onCancel={() => setMergeSource(null)}
                    onConfirmPost={(target, delta) => mergeIntoPost(mergeSource, target, delta)}
                    onConfirmTopic={(topicSlug, delta) => mergeIntoTopic(mergeSource, topicSlug, delta)}
                />
            )}
            {textPrompt && (
                <TextPromptModal {...textPrompt}
                    onCancel={() => setTextPrompt(null)}
                    onConfirm={(v) => { textPrompt.onConfirm(v); setTextPrompt(null); }} />
            )}
            {confirmPrompt && (
                <ConfirmModal {...confirmPrompt}
                    onCancel={() => setConfirmPrompt(null)}
                    onConfirm={() => { confirmPrompt.onConfirm(); setConfirmPrompt(null); }} />
            )}
            {previewPostID && (
                <PostPreviewModal postID={previewPostID}
                    api={api}
                    onClose={() => setPreviewPostID(null)} />
            )}
            {queueSettingsOpen && (
                <QueueSettingsModal
                    slug={slug!}
                    api={api}
                    onClose={() => setQueueSettingsOpen(false)}
                />
            )}
        </main>
    );
}

// groupByDay slices the queue into day-buckets (newest day first) so the
// column can render section headers.
function groupByDay(items: QueueItem[]): Array<{ day: string; items: QueueItem[] }> {
    const out: Array<{ day: string; items: QueueItem[] }> = [];
    for (const it of items) {
        const d = new Date(it.submitted_at);
        const key = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
        const last = out[out.length - 1];
        if (last && last.day === key) last.items.push(it);
        else out.push({ day: key, items: [it] });
    }
    return out;
}


// ---- Column + cards ----

function Column({ title, hint, children, onHeaderClick }: {
    title: string; hint: string; children: React.ReactNode;
    onHeaderClick?: () => void;
}) {
    const clickable = !!onHeaderClick;
    return (
        <div style={colStyle}>
            <div
                style={{ ...colHead, cursor: clickable ? 'pointer' : 'default' }}
                onClick={onHeaderClick}
                title={clickable ? 'Click to open settings' : undefined}
            >
                <div style={{ fontFamily: 'Newsreader, serif', fontSize: 20 }}>
                    {title}
                    {clickable && <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>⚙</span>}
                </div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{hint}</div>
            </div>
            <div style={colBody}>{children}</div>
        </div>
    );
}

function Empty({ text }: { text: string }) {
    return <div style={{ padding: 20, color: '#999', fontStyle: 'italic', textAlign: 'center', fontSize: 13 }}>{text}</div>;
}

// LiveIndicator — subtle "· live · updated 5s ago" nav pill so the editor
// can tell polling is actually happening. Re-renders every 1s off a local
// tick so the "Xs ago" ticks up smoothly between server fetches.
function LiveIndicator({ lastLoadedAt }: { lastLoadedAt: number | null }) {
    const [, force] = useState(0);
    useEffect(() => {
        const t = setInterval(() => force(n => n + 1), 1_000);
        return () => clearInterval(t);
    }, []);
    if (!lastLoadedAt) return null;
    const secs = Math.max(0, Math.round((Date.now() - lastLoadedAt) / 1000));
    return (
        <span
            title="Newsroom auto-refreshes every 15s while the tab is visible"
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11, color: '#666', fontFamily: '-apple-system, sans-serif',
                textTransform: 'none', letterSpacing: 0,
            }}
        >
            <span style={{
                width: 6, height: 6, background: '#3a5c2b', borderRadius: '50%',
                display: 'inline-block',
            }} />
            live · {secs}s ago
        </span>
    );
}

function FeedCard({ item }: { item: FeedItem }) {
    // "Relevance clock" = when source published it. Fallback to fetched
    // if source didn't send a date (rare but happens with some RSS feeds).
    const relTime = item.published_at ?? item.fetched_at;
    const relLabel = item.published_at ? 'published' : 'fetched';
    const [expanded, setExpanded] = useState(false);
    const hasFullBody = !!item.body && item.body.length > 180;
    return (
        <div
            style={{ ...cardStyle, cursor: hasFullBody ? 'pointer' : 'default' }}
            onClick={hasFullBody ? () => setExpanded(v => !v) : undefined}
            title={hasFullBody ? (expanded ? 'Click to collapse' : 'Click to read full item') : undefined}
        >
            <div style={metaLine}>
                <span style={sourceBadge}>{item.source_kind}</span>
                <span style={{ fontWeight: 600 }}>{item.source_name}</span>
                <span style={{ marginLeft: 'auto', color: '#888', fontSize: 11 }} title={`${relLabel} ${new Date(relTime).toLocaleString()}`}>
                    {timeAgo(relTime)}
                </span>
            </div>
            <div style={{ fontWeight: 600, marginTop: 4 }}>
                {item.link ? (
                    <a href={item.link} target="_blank" rel="noreferrer"
                       onClick={e => e.stopPropagation()}
                       style={{ color: 'inherit', textDecoration: 'underline dotted' }}
                       title="Open source in new tab">
                        {item.title}
                    </a>
                ) : item.title}
            </div>
            {item.body && (
                <div style={{ fontSize: 12, color: '#555', marginTop: 4, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                    {expanded ? item.body : trim(item.body, 180)}
                    {hasFullBody && (
                        <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>
                            {expanded ? '· click to collapse' : '· click for full story'}
                        </span>
                    )}
                </div>
            )}
            <div style={{ marginTop: 4, display: 'flex', gap: 8, fontSize: 11, color: '#888' }}>
                {item.published_at && (
                    <span title={new Date(item.published_at).toLocaleString()}>
                        📰 {timeAgo(item.published_at)}
                    </span>
                )}
                <span title={new Date(item.fetched_at).toLocaleString()}>
                    🔄 {timeAgo(item.fetched_at)}
                </span>
                {item.link && (
                    <a
                        href={item.link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginLeft: 'auto', color: '#888' }}
                    >source →</a>
                )}
            </div>
        </div>
    );
}

// ActivityStrip — compact "what your agents just did" list rendered above
// the kanban board. Each row: time · agent · action. Links to the created
// post (if agent published) or the source (if agent triaged a feed item).
function ActivityStrip({ activity, onRetry }: {
    activity: AgentActivity[];
    onRetry: (feedItemID: string) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    if (activity.length === 0) {
        return (
            <div style={activityBoxStyle}>
                <div style={activityHeadStyle}>
                    <strong>Agent activity</strong>
                    <span style={{ color: '#888', fontSize: 11 }}>· nothing in the last 30 days</span>
                </div>
            </div>
        );
    }
    const shown = expanded ? activity : activity.slice(0, 6);
    return (
        <div style={activityBoxStyle}>
            <div style={activityHeadStyle}>
                <strong>Agent activity</strong>
                <span style={{ color: '#888', fontSize: 11 }}>· last {activity.length}</span>
                {activity.length > 6 && (
                    <button
                        type="button" className="plain"
                        onClick={() => setExpanded(v => !v)}
                        style={{ marginLeft: 'auto', fontSize: 11 }}
                    >{expanded ? 'show less' : `show all ${activity.length}`}</button>
                )}
            </div>
            <div>
                {shown.map((ev, i) => {
                    const k = kindMeta(ev.kind);
                    return (
                        <div key={i} style={activityRowStyle}>
                            <span style={activityTimeStyle} title={new Date(ev.time).toLocaleString()}>
                                {timeAgo(ev.time)}
                            </span>
                            <span style={activityAgentStyle}>{ev.agent}</span>
                            <span style={{ ...activityKindStyle, background: k.bg, color: k.fg, borderColor: k.border }}>
                                {k.label}
                            </span>
                            <span style={{ flex: 1, color: '#333' }}>
                                {ev.title}
                                {ev.reason && (
                                    <span style={{ color: '#888', marginLeft: 6 }}>· {ev.reason}</span>
                                )}
                            </span>
                            {ev.post_id && (
                                <Link to={'/posts/' + ev.post_id} style={activityLinkStyle}>post →</Link>
                            )}
                            {!ev.post_id && ev.link && (
                                <a href={ev.link} target="_blank" rel="noreferrer" style={activityLinkStyle}>source →</a>
                            )}
                            {ev.feed_item_id && ev.kind !== 'cost' && (
                                <button
                                    type="button"
                                    onClick={() => onRetry(ev.feed_item_id!)}
                                    style={activityRetryStyle}
                                    title="Clear triage + geocode; reporter will re-process on next tick"
                                >retry</button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// kindMeta maps a triage_outcome / cost / published value to a coloured
// pill label. Colours: green for shipped, amber for skipped, red for
// parse errors, grey for cost telemetry.
function kindMeta(kind: string): { label: string; bg: string; fg: string; border: string } {
    switch (kind) {
        case 'published':            return { label: 'PUBLISHED',   bg: '#e6f0e0', fg: '#3a5c2b', border: '#88a877' };
        case 'updated':              return { label: 'UPDATED',     bg: '#e6f0e0', fg: '#3a5c2b', border: '#88a877' };
        case 'skipped_duplicate':    return { label: 'DUP',         bg: '#fbf3d9', fg: '#7a6817', border: '#c9b877' };
        case 'skipped_not_relevant': return { label: 'SKIPPED',     bg: '#fbf3d9', fg: '#7a6817', border: '#c9b877' };
        case 'skipped_too_old':      return { label: 'OLD',         bg: '#fbf3d9', fg: '#7a6817', border: '#c9b877' };
        case 'parse_error':          return { label: 'ERROR',       bg: '#f5dcdc', fg: '#8b1919', border: '#c48a8a' };
        case 'cost':                 return { label: 'RAN',         bg: '#eee',    fg: '#666',    border: '#ccc'    };
        case 'reviewed':             return { label: 'REVIEWED',    bg: '#eee',    fg: '#666',    border: '#ccc'    };
        default:                     return { label: kind.toUpperCase(), bg: '#eee', fg: '#666', border: '#ccc'    };
    }
}

function QueueCard(p: {
    item: QueueItem;
    onApprove: () => void; onReject: () => void; onReturn: () => void; onMerge: () => void;
    onArchive: () => void; onDropImage: () => void; onReplaceImage: () => void;
    onPreview: () => void;
}) {
    // Card body opens the preview modal on click; media + action-button
    // regions stopPropagation so their in-card affordances still work.
    return (
        <div style={cardStyle}>
            {p.item.media_url && (
                <div
                    style={{ position: 'relative', marginBottom: 6, cursor: 'zoom-in' }}
                    onClick={p.onPreview}
                    title="Click to preview full post"
                >
                    <img src={p.item.media_url} alt="" style={cardImg} loading="lazy" />
                    <div style={imgOverlay} onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={p.onDropImage} style={imgBtn}>×</button>
                        <button type="button" onClick={p.onReplaceImage} style={imgBtn}>swap</button>
                    </div>
                </div>
            )}
            {!p.item.media_url && (
                <button type="button" onClick={p.onReplaceImage} style={addImgBtn}>+ add cover image</button>
            )}
            <div
                onClick={p.onPreview}
                style={{ cursor: 'pointer' }}
                title="Click to preview full post"
            >
                <div style={metaLine}>
                    <span style={{ fontWeight: 600 }}>{p.item.author_display}</span>
                    {p.item.author_is_bot && <span style={botBadge}>BOT</span>}
                    {p.item.topic && <span className="topic-flair">{p.item.topic.replace(/_/g, ' ')}</span>}
                    {p.item.update_count > 0 && <span style={updBadge}>+{p.item.update_count}</span>}
                    <span style={{ marginLeft: 'auto', color: '#888', fontSize: 11 }}>{timeAgo(p.item.submitted_at)}</span>
                </div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{p.item.title}</div>
                {p.item.summary && <div style={summaryStyle}>{p.item.summary}</div>}
                <div style={{ fontSize: 12, color: '#555', marginTop: 4, lineHeight: 1.4 }}>{trim(p.item.body_snippet, 220)}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                <button type="button" className="primary" onClick={p.onApprove} style={actionBtn}>Approve</button>
                <button type="button" className="plain"   onClick={p.onReject}  style={actionBtn}>Reject</button>
                <button type="button" className="plain"   onClick={p.onReturn}  style={actionBtn}>Return</button>
                <button type="button" className="plain"   onClick={p.onMerge}   style={actionBtn}>Merge into…</button>
                <button type="button" className="plain"   onClick={p.onArchive} style={actionBtn} title="Archive — remove from queue">🗄</button>
                <Link to={`/posts/${p.item.post_id}`} style={{ ...actionBtn, textDecoration: 'none', color: '#555' }} title="Open in full reader">open ↗</Link>
            </div>
        </div>
    );
}

// ---- Merge modal ----

function MergeModal(p: {
    source:         QueueItem;
    candidates:     Array<QueueItem | LiveItem>;
    topics:         Topic[];
    onCancel:       () => void;
    onConfirmPost:  (target: QueueItem | LiveItem, deltaText: string) => void;
    onConfirmTopic: (topicSlug: string, deltaText: string) => void;
}) {
    const [mode, setMode] = useState<'topic'|'post'>('topic');
    const [targetID, setTargetID] = useState('');
    const [topicSlug, setTopicSlug] = useState('');
    const [delta,    setDelta]    = useState('');
    const targetPost = useMemo(() => p.candidates.find(c => c.post_id === targetID), [p.candidates, targetID]);
    return (
        <div style={modalBackdrop} onClick={p.onCancel}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 8px' }}>Merge</h3>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                    Source: <strong>{p.source.title}</strong>
                </div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12 }}>
                    <label>
                        <input type="radio" checked={mode === 'topic'} onChange={() => setMode('topic')} /> Into topic
                    </label>
                    <label>
                        <input type="radio" checked={mode === 'post'} onChange={() => setMode('post')} /> Into specific post
                    </label>
                </div>
                {mode === 'topic' && (
                    <>
                        <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Topic</label>
                        <select value={topicSlug} onChange={e => setTopicSlug(e.target.value)}
                            style={{ width: '100%', padding: 6, fontFamily: 'inherit', marginBottom: 4 }}>
                            <option value="">— pick a topic —</option>
                            {p.topics.map(t => (
                                <option key={t.slug} value={t.slug}>
                                    {t.name} ({t.slug})
                                </option>
                            ))}
                        </select>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
                            If today's article for this topic exists, source merges into it. Otherwise the source becomes the day's article for this topic.
                        </div>
                    </>
                )}
                {mode === 'post' && (
                    <>
                        <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Target post</label>
                        <select value={targetID} onChange={e => setTargetID(e.target.value)}
                            style={{ width: '100%', padding: 6, fontFamily: 'inherit', marginBottom: 12 }}>
                            <option value="">— pick a post —</option>
                            {p.candidates.map(c => (
                                <option key={c.post_id} value={c.post_id}>
                                    {'title' in c ? c.title : ''}
                                </option>
                            ))}
                        </select>
                    </>
                )}
                <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Update text (optional)</label>
                <textarea value={delta} onChange={e => setDelta(e.target.value)} rows={3}
                    placeholder="e.g. Also affects Molteno Rd. Leave blank to use the source post's own body."
                    style={{ width: '100%', padding: 8, fontFamily: 'inherit', fontSize: 13, marginBottom: 12 }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" className="plain" onClick={p.onCancel}>Cancel</button>
                    <button type="button" className="primary"
                        disabled={mode === 'post' ? !targetPost : !topicSlug}
                        onClick={() => {
                            if (mode === 'post' && targetPost) p.onConfirmPost(targetPost, delta);
                            if (mode === 'topic' && topicSlug) p.onConfirmTopic(topicSlug, delta);
                        }}>Merge</button>
                </div>
            </div>
        </div>
    );
}

// PostPreviewModal — click a queue/live card to open the full post
// (title, kicker, byline, media, markdown body, map pins) without
// leaving the newsroom kanban. Fetches /api/posts/:id lazily.
// Escape or backdrop click closes it. "Open in full reader ↗" navigates
// to /posts/:id for the complete experience with comments.
function PostPreviewModal({ postID, api, onClose }: {
    postID: string;
    api: ReturnType<typeof useApi>;
    onClose: () => void;
}) {
    const [post, setPost] = useState<Post | null>(null);
    const [err,  setErr]  = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const p = await api.get<Post>('/api/posts/' + postID);
                if (!cancelled) setPost(p);
            } catch (e) {
                if (!cancelled) setErr((e as Error).message);
            }
        })();
        return () => { cancelled = true; };
    }, [api, postID]);

    // Close on Escape — matches the other newsroom modals' feel.
    useEffect(() => {
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div style={modalBackdrop} onClick={onClose}>
            <div
                style={{ ...modalCard, maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', padding: 0 }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid #cfc9b7', background: '#f2eee0', position: 'sticky', top: 0, zIndex: 1 }}>
                    <strong style={{ fontSize: 13 }}>Preview</strong>
                    <Link to={`/posts/${postID}`} style={{ fontSize: 12, color: '#555', marginLeft: 'auto' }}>
                        open in full reader ↗
                    </Link>
                    <button type="button" onClick={onClose} className="plain" style={{ padding: '0 8px', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
                <div style={{ padding: '16px 20px' }}>
                    {err && <p style={{ color: '#8b1919' }}>Couldn't load post: {err}</p>}
                    {!err && !post && <p style={{ color: '#666', fontStyle: 'italic' }}>Loading…</p>}
                    {post && (
                        <>
                            <EditorBar postID={post.id} />
                            {post.kicker && (
                                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: '#8b1919', marginBottom: 6 }}>
                                    {post.kicker}
                                </div>
                            )}
                            <h1 style={{ fontFamily: 'Newsreader, serif', fontSize: 28, margin: '0 0 4px', lineHeight: 1.15 }}>
                                {post.title || '(untitled)'}
                            </h1>
                            {post.subhead && (
                                <p style={{ color: '#555', margin: '0 0 8px', fontStyle: 'italic' }}>{post.subhead}</p>
                            )}
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                                By {post.display_identity} · {timeAgo(post.created_at)}
                                {post.topic && <> · <span className="topic-flair">{post.topic.replace(/_/g, ' ')}</span></>}
                            </div>
                            {post.media_url && (
                                <img src={post.media_url} alt="" style={{ display: 'block', width: '100%', margin: '0 0 12px' }} />
                            )}
                            <div
                                style={{ fontFamily: 'Newsreader, serif', fontSize: 16, lineHeight: 1.55, color: '#222' }}
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
                            />
                            {post.pins && post.pins.length > 0 && (
                                <div style={{ marginTop: 12 }}>
                                    <PostMap pins={post.pins} />
                                </div>
                            )}
                            {(!post.pins || post.pins.length === 0) && typeof post.lat === 'number' && typeof post.lng === 'number' && post.lat !== 0 && (
                                <div style={{ marginTop: 12 }}>
                                    <PostMap pins={[{ lat: post.lat, lng: post.lng, label: post.title || undefined }]} height={200} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function trim(s: string, n: number) { return s.length > n ? s.slice(0, n) + '…' : s; }

// ---- Reusable modals ----

function TextPromptModal(p: {
    title: string; placeholder?: string; confirmLabel?: string;
    onCancel: () => void; onConfirm: (value: string) => void;
}) {
    const [value, setValue] = useState('');
    return (
        <div style={modalBackdrop} onClick={p.onCancel}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px' }}>{p.title}</h3>
                <textarea autoFocus value={value} onChange={e => setValue(e.target.value)}
                    placeholder={p.placeholder} rows={3}
                    style={{ width: '100%', padding: 8, fontFamily: 'inherit', fontSize: 13, marginBottom: 12,
                             border: '1px solid #cfc9b7' }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { p.onConfirm(value); }
                        if (e.key === 'Escape') { p.onCancel(); }
                    }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#999', marginRight: 'auto' }}>⌘⏎ to confirm · esc to cancel</span>
                    <button type="button" className="plain" onClick={p.onCancel}>Cancel</button>
                    <button type="button" className="primary" onClick={() => p.onConfirm(value)}>
                        {p.confirmLabel ?? 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// QueueSettingsModal — opens when the editor clicks the Queue column
// header. Lists every reporter agent scoped to this community with an
// autopublish toggle. Autopublish flips a per-agent flag so that
// agent's future submissions skip the pending queue and land straight
// in Live. Community "posting_policy" is a bigger hammer that also
// auto-approves human submissions; this is the lighter, bot-only knob.
function QueueSettingsModal(p: {
    slug: string;
    api: ReturnType<typeof useApi>;
    onClose: () => void;
}) {
    const [reporters, setReporters] = useState<Reporter[] | null>(null);
    const [err, setErr] = useState('');
    const [pending, setPending] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await p.api.get<{ reporters: Reporter[] }>(
                    `/api/communities/${p.slug}/reporters`);
                if (!cancelled) setReporters(res.reporters ?? []);
            } catch (e) {
                if (!cancelled) setErr((e as Error).message);
            }
        })();
        return () => { cancelled = true; };
    }, [p.api, p.slug]);

    useEffect(() => {
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') p.onClose(); }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [p]);

    async function toggle(r: Reporter) {
        const next = !r.autopublish;
        setPending(s => ({ ...s, [r.agent_id]: true }));
        try {
            await p.api.patch(
                `/api/communities/${p.slug}/reporters/${r.agent_id}/autopublish`,
                { autopublish: next });
            setReporters(list => list?.map(x =>
                x.agent_id === r.agent_id ? { ...x, autopublish: next } : x) ?? null);
        } catch (e) {
            setErr((e as Error).message);
        } finally {
            setPending(s => { const n = { ...s }; delete n[r.agent_id]; return n; });
        }
    }

    return (
        <div style={modalBackdrop} onClick={p.onClose}>
            <div style={{ ...modalCard, width: 520 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>Queue settings</h3>
                    <button type="button" onClick={p.onClose} className="plain"
                        style={{ marginLeft: 'auto', padding: '0 8px', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
                <p style={{ fontSize: 12, color: '#555', margin: '0 0 16px' }}>
                    Autopublish lets a reporter's drafts skip the pending queue and
                    land in Live immediately. Only affects that agent — human
                    submissions still follow the community's posting policy.
                </p>
                {err && <p style={{ color: '#8b1919', fontSize: 12 }}>{err}</p>}
                {!err && reporters === null && (
                    <p style={{ color: '#666', fontStyle: 'italic', fontSize: 13 }}>Loading…</p>
                )}
                {reporters && reporters.length === 0 && (
                    <p style={{ color: '#666', fontStyle: 'italic', fontSize: 13 }}>
                        No reporters attached to this community yet.
                    </p>
                )}
                {reporters && reporters.map(r => (
                    <div key={r.agent_id} style={reporterRowStyle}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.agent_name}</div>
                            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                                {r.role}
                                {!r.active && <span style={{ color: '#8b1919', marginLeft: 6 }}>· inactive</span>}
                                {r.paused_reason && <span style={{ color: '#8b1919', marginLeft: 6 }}>· paused: {r.paused_reason}</span>}
                            </div>
                        </div>
                        <label style={toggleWrapStyle} title={r.autopublish ? 'Click to require moderation again' : 'Click to skip the queue'}>
                            <input
                                type="checkbox"
                                checked={r.autopublish}
                                disabled={!!pending[r.agent_id]}
                                onChange={() => toggle(r)}
                                style={{ marginRight: 6 }}
                            />
                            <span style={{ fontSize: 12 }}>
                                autopublish {r.autopublish ? 'ON' : 'OFF'}
                            </span>
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ConfirmModal(p: {
    title: string; detail?: string; confirmLabel?: string; danger?: boolean;
    onCancel: () => void; onConfirm: () => void;
}) {
    return (
        <div style={modalBackdrop} onClick={p.onCancel}>
            <div style={modalCard} onClick={e => e.stopPropagation()}
                onKeyDown={e => { if (e.key === 'Escape') p.onCancel(); }}
            >
                <h3 style={{ margin: '0 0 8px' }}>{p.title}</h3>
                {p.detail && <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>{p.detail}</p>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" className="plain" onClick={p.onCancel}>Cancel</button>
                    <button type="button" className={p.danger ? 'primary' : 'primary'}
                        onClick={p.onConfirm}
                        style={p.danger ? { background: '#8b1919', color: '#fff', borderColor: '#8b1919' } : undefined}>
                        {p.confirmLabel ?? 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const dayHeader: React.CSSProperties = {
    padding: '4px 8px', margin: '10px 0 4px', fontFamily: 'Newsreader, serif',
    fontSize: 13, color: '#666', textTransform: 'uppercase', letterSpacing: 1.5,
    borderBottom: '1px solid #cfc9b7',
};

// ---- Styles ----

const navStyle: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '8px 0', marginBottom: 12,
    borderBottom: '1px solid #cfc9b7',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 12, textTransform: 'uppercase', letterSpacing: 1,
    alignItems: 'center',
};
const navLink: React.CSSProperties = { color: '#333', textDecoration: 'none', padding: '4px 10px' };
const boardStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12,
    alignItems: 'start',
};
const colStyle: React.CSSProperties = {
    border: '1px solid #cfc9b7', background: '#faf8f2', minHeight: 400,
    display: 'flex', flexDirection: 'column',
};
const colHead: React.CSSProperties = {
    padding: '10px 12px', borderBottom: '1px solid #cfc9b7', background: '#f2eee0',
};
const colBody: React.CSSProperties = {
    padding: 8, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', maxHeight: 'calc(100vh - 240px)',
};
const cardStyle: React.CSSProperties = {
    padding: 10, background: '#fff', border: '1px solid #e5e0cf', fontSize: 13, lineHeight: 1.4,
};
const cardImg: React.CSSProperties = {
    width: '100%', height: 110, objectFit: 'cover', display: 'block', border: '1px solid #e5e0cf',
};
const imgOverlay: React.CSSProperties = {
    position: 'absolute', top: 4, right: 4, display: 'flex', gap: 4,
};
const imgBtn: React.CSSProperties = {
    background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', padding: '2px 8px',
    fontSize: 11, cursor: 'pointer',
};
const addImgBtn: React.CSSProperties = {
    display: 'block', width: '100%', padding: '8px', background: '#f2eee0',
    border: '1px dashed #cfc9b7', color: '#666', fontSize: 12, cursor: 'pointer', marginBottom: 6,
};
const metaLine: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
};
const sourceBadge: React.CSSProperties = {
    fontFamily: 'monospace', fontSize: 10, padding: '1px 5px', background: '#f2eee0',
    border: '1px solid #cfc9b7', textTransform: 'uppercase',
};
const botBadge: React.CSSProperties = {
    fontFamily: 'monospace', fontSize: 10, padding: '1px 5px', background: '#e6f0e0',
    border: '1px solid #88a877', color: '#3a5c2b',
};
const updBadge: React.CSSProperties = {
    fontFamily: 'monospace', fontSize: 10, padding: '1px 5px', background: '#fbf3d9',
    border: '1px solid #c9b877', color: '#7a6817',
};
const actionBtn: React.CSSProperties = { padding: '4px 10px', fontSize: 12 };
const activityBoxStyle: React.CSSProperties = {
    border: '1px solid #cfc9b7', background: '#faf8f2', marginBottom: 12,
    fontFamily: '-apple-system, sans-serif', fontSize: 12,
};
const activityHeadStyle: React.CSSProperties = {
    padding: '8px 12px', borderBottom: '1px solid #cfc9b7', background: '#f2eee0',
    display: 'flex', alignItems: 'center', gap: 6,
};
const activityRowStyle: React.CSSProperties = {
    display: 'flex', gap: 10, alignItems: 'baseline',
    padding: '6px 12px', borderBottom: '1px dotted #e5e0cf',
    lineHeight: 1.4,
};
const activityTimeStyle: React.CSSProperties = {
    fontFamily: 'monospace', fontSize: 11, color: '#888', minWidth: 60,
};
const activityAgentStyle: React.CSSProperties = {
    fontWeight: 600, color: '#3a5c2b', minWidth: 120,
};
const activityLinkStyle: React.CSSProperties = {
    color: '#888', textDecoration: 'none', fontSize: 11,
};
const activityKindStyle: React.CSSProperties = {
    fontFamily: 'monospace', fontSize: 10, padding: '1px 6px',
    border: '1px solid transparent', letterSpacing: 1, minWidth: 70,
    textAlign: 'center', textTransform: 'uppercase',
};
const activityRetryStyle: React.CSSProperties = {
    fontFamily: '-apple-system, sans-serif', fontSize: 11,
    padding: '2px 8px', background: '#f2eee0',
    border: '1px solid #cfc9b7', cursor: 'pointer',
    color: '#333',
};
const summaryStyle: React.CSSProperties = {
    fontStyle: 'italic', fontSize: 12, color: '#666', marginTop: 4,
};
const modalBackdrop: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalCard: React.CSSProperties = {
    background: '#fff', padding: 20, width: 480, maxWidth: '90vw',
    border: '1px solid #cfc9b7',
};
const reporterRowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 0', borderTop: '1px solid #e5e0cf',
};
const toggleWrapStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', cursor: 'pointer',
    padding: '4px 8px', border: '1px solid #cfc9b7', background: '#faf8f2',
};
